import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { DrawerForm } from "../Common/styles/drawer";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import isEmail from "validator/es/lib/isEmail";
import { countries } from "../../const/countries";

const activeOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];
let schema = yup.object().shape({
  sponsorName: yup.string().required("Sponsor Name is required").trim(),
  sponsorType: yup.string().required("Sponsor Type is required").trim(),
  addressStreet: yup.string().optional(),
  addressCity: yup.string().optional(),
  addressState: yup.string().optional(),
  addressZip: yup
    .string()
    .matches(/^[A-Za-z0-9\s\-]{3,10}$/, "Enter a valid ZIP Code")
    .trim(),
  addressCountry: yup.string().required("Country is required").trim(),
  contactName: yup.string().optional(),
  contactEmail: yup
    .string()
    .trim()
    .required("Email is Required")
    .test("is-valid", "*Please enter a valid Email ID.", (value) =>
      value ? isEmail(value) : false
    ),
  contactPhone: yup
    .string()
    .optional()
    .matches(/^[0-9+\-\s()]{7,20}$/, "Enter a valid phone number")
    .trim(),
  websiteUrl: yup.string().url("Enter a valid Website URL").optional().trim(),
  isActive: yup.string().optional(),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
  data: any;
  refreshPage: () => void;
};

const AddSponsorModal = ({
  showModal,
  closeModal,
  data,
  refreshPage,
}: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      let res: AxiosResponse;
      const body = {
        ...values,
        isActive: values?.isActive === "yes",
        addressCountry: values?.addressCountry?.toString(),
      };
      if (data?.id) {
        res = await http.patch(`/admin/registry/sponsors/${data?.id}`, body);
      } else {
        res = await http.post(`/admin/registry/sponsors`, body);
      }

      toastMessage("success", res?.data?.message);
      closeModal();
      setSubmitLoader(false);
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={data?.id ? "Edit Client/Sponsor" : "Add Client/Sponsor"}
          onCloseClick={closeModal}
        />

        <Formik
          initialValues={{
            sponsorName: data?.sponsorName || "",
            sponsorType: data?.sponsorType || "",
            addressStreet: data?.addressStreet || "",
            addressCity: data?.addressCity || "",
            addressState: data?.addressState || "",
            addressZip: data?.addressZip || "",
            addressCountry: data?.addressCountry || "",
            contactName: data?.contactName || "",
            contactEmail: data?.contactEmail || "",
            contactPhone: data?.contactPhone || "",
            websiteUrl: data?.websiteUrl || "",
            isActive: data?.isActive ? "yes" : "no",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <Box>
                <Typography
                  fontWeight={"regular"}
                  variant="h3"
                  color="text.primary"
                  mb={2.5}
                >
                  Client/Sponsor Details
                </Typography>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="sponsorName">
                    Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Client/Sponsor Name"
                    id="sponsorName"
                    {...getFieldProps("sponsorName")}
                    error={
                      touched?.sponsorName && errors?.sponsorName ? true : false
                    }
                    helperText={
                      touched?.sponsorName && errors?.sponsorName
                        ? (errors?.sponsorName as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="sponsorType">
                    Type <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Client/Sponsor Type"
                    id="sponsorType"
                    {...getFieldProps("sponsorType")}
                    error={
                      touched?.sponsorType && errors?.sponsorType ? true : false
                    }
                    helperText={
                      touched?.sponsorType && errors?.sponsorType
                        ? (errors?.sponsorType as string)
                        : " "
                    }
                  />
                </FormControl>

                <Typography
                  fontWeight={"regular"}
                  variant="h3"
                  color="text.primary"
                  mb={2.5}
                >
                  Address Details
                </Typography>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="addressStreet">
                    Street
                  </FormLabel>
                  <TextField
                    placeholder="Street"
                    id="addressStreet"
                    {...getFieldProps("addressStreet")}
                    error={
                      touched?.addressStreet && errors?.addressStreet
                        ? true
                        : false
                    }
                    helperText={
                      touched?.addressStreet && errors?.addressStreet
                        ? (errors?.addressStreet as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="addressCity">
                    City
                  </FormLabel>
                  <TextField
                    placeholder="City"
                    id="addressCity"
                    {...getFieldProps("addressCity")}
                    error={
                      touched?.addressCity && errors?.addressCity ? true : false
                    }
                    helperText={
                      touched?.addressCity && errors?.addressCity
                        ? (errors?.addressCity as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="addressState">
                    State
                  </FormLabel>
                  <TextField
                    placeholder="State"
                    id="addressState"
                    {...getFieldProps("addressState")}
                    error={
                      touched?.addressState && errors?.addressState
                        ? true
                        : false
                    }
                    helperText={
                      touched?.addressState && errors?.addressState
                        ? (errors?.addressState as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="addressZip">
                    Zip
                  </FormLabel>
                  <TextField
                    placeholder="Zip"
                    id="addressZip"
                    {...getFieldProps("addressZip")}
                    error={
                      touched?.addressZip && errors?.addressZip ? true : false
                    }
                    helperText={
                      touched?.addressZip && errors?.addressZip
                        ? (errors?.addressZip as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="addressCountry">
                    Country <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    fullWidth
                    value={values.addressCountry || ""}
                    onChange={(e) =>
                      setFieldValue("addressCountry", e.target.value)
                    }
                  >
                    {countries.map((country: any) => (
                      <MenuItem value={country.value} key={country.value}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={
                      touched?.addressCountry && errors?.addressCountry
                        ? true
                        : false
                    }
                  >
                    {touched?.addressCountry && errors?.addressCountry
                      ? (errors?.addressCountry as string)
                      : " "}
                  </FormHelperText>
                </FormControl>

                <Typography
                  fontWeight={"regular"}
                  variant="h3"
                  color="text.primary"
                  mb={2.5}
                >
                  Contact Details
                </Typography>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="contactName">
                    Name
                  </FormLabel>
                  <TextField
                    placeholder="Name"
                    id="contactName"
                    {...getFieldProps("contactName")}
                    error={
                      touched?.contactName && errors?.contactName ? true : false
                    }
                    helperText={
                      touched?.contactName && errors?.contactName
                        ? (errors?.contactName as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="contactEmail">
                    Email <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Email"
                    id="contactEmail"
                    {...getFieldProps("contactEmail")}
                    error={
                      touched?.contactEmail && errors?.contactEmail
                        ? true
                        : false
                    }
                    helperText={
                      touched?.contactEmail && errors?.contactEmail
                        ? (errors?.contactEmail as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={{ ...InputWrapper, mb: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="contactPhone">
                    Phone Number
                  </FormLabel>
                  <PhoneInput
                    country={"us"}
                    inputStyle={{
                      height: "48px",
                      width: "100%",
                      borderColor: "#e0e3eb",
                    }}
                    value={values.contactPhone}
                    onChange={(phone) => setFieldValue("contactPhone", phone)}
                  />
                  <FormHelperText error={true}>
                    {errors?.contactPhone as string}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="websiteUrl">
                    Website Url
                  </FormLabel>
                  <TextField
                    placeholder="Website Url"
                    id="websiteUrl"
                    {...getFieldProps("websiteUrl")}
                    error={
                      touched?.websiteUrl && errors?.websiteUrl ? true : false
                    }
                    helperText={
                      touched?.websiteUrl && errors?.websiteUrl
                        ? (errors?.websiteUrl as string)
                        : " "
                    }
                  />
                </FormControl>

                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="isActive">
                    Is Active
                  </FormLabel>
                  <Select
                    value={values?.isActive}
                    onChange={(e) => setFieldValue("isActive", e.target.value)}
                  >
                    {activeOptions?.map((opt) => (
                      <MenuItem key={opt.label} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {touched?.isActive && errors?.isActive
                      ? (errors?.isActive as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        {data?.id ? "Save" : "Add"}
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddSponsorModal;
